import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiUrl } from '../config/api';
import { MIN_PASSWORD_LENGTH } from '../constants/registration';
import { apiFetch, updateSessionUser } from '../utils/session';
import PasswordInput from '../components/PasswordInput';
import ProfileDetails from '../components/ProfileDetails';
import SelfieImage from '../components/SelfieImage';
import './MyProfile.css';

const STATUS_COPY = {
  Pending: {
    badge: 'Pending approval',
    title: 'Your application is under review',
    text: 'An admin is verifying your details and documents. Once approved, you can view and edit your profile and download your member ID card here.',
  },
  Rejected: {
    badge: 'Rejected',
    title: 'Your application was not approved',
    text: 'Please contact your Area Head or the Sangram Sena office with your reference ID if you think this is a mistake.',
  },
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function ApplicationStatus({ profile, onRefresh, refreshing }) {
  const copy = STATUS_COPY[profile.status] || STATUS_COPY.Pending;
  const badgeClass = profile.status === 'Active' ? 'active' : profile.status === 'Rejected' ? 'rejected' : 'pending';
  return (
    <div className={`prof-status-card${profile.status === 'Pending' ? ' pending' : ''}${profile.status === 'Rejected' ? ' rejected' : ''}`}>
      <span className={`prof-badge ${badgeClass}`}>{copy.badge}</span>
      <h2 className="prof-status-title">{copy.title}</h2>
      <p className="prof-status-text">{copy.text}</p>

      <dl className="prof-status-meta">
        <div>
          <dt>Name</dt>
          <dd>{profile.name}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd>{profile.email}</dd>
        </div>
        <div>
          <dt>Your reference ID (for office / support)</dt>
          <dd className="prof-mono">{profile.referenceNumber || '—'}</dd>
        </div>
        <div>
          <dt>Applied on</dt>
          <dd>{formatDate(profile.createdAt)}</dd>
        </div>
      </dl>

      {profile.status === 'Pending' ? (
        <button type="button" className="prof-btn ghost" onClick={onRefresh} disabled={refreshing}>
          {refreshing ? 'Checking…' : 'Check status again'}
        </button>
      ) : null}
    </div>
  );
}

function ChangePassword({ onDone }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (newPassword !== confirm) {
      setError('New passwords do not match');
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch(apiUrl('/profile/password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not change password');
        return;
      }
      onDone('Password changed.');
    } catch {
      setError('Network error. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="prof-password" onSubmit={submit}>
      <h3 className="prof-panel-title">Change password</h3>
      {error ? (
        <p className="prof-error" role="alert">
          {error}
        </p>
      ) : null}
      <label className="prof-field">
        Current password
        <PasswordInput
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
      </label>
      <label className="prof-field">
        New password
        <PasswordInput
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
      </label>
      <label className="prof-field">
        Confirm new password
        <PasswordInput
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          required
        />
      </label>
      <div className="prof-row-end">
        <button type="button" className="prof-btn ghost" onClick={() => onDone('')} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="prof-btn primary" disabled={saving}>
          {saving ? 'Saving…' : 'Update password'}
        </button>
      </div>
    </form>
  );
}

function formatWhen(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function SosHistory({ alerts, loading }) {
  if (loading) return <p className="prof-muted">Loading SOS history…</p>;
  if (!alerts.length) {
    return <p className="prof-muted">You have not sent any SOS alerts yet.</p>;
  }
  return (
    <ul className="prof-sos-list">
      {alerts.map((alert) => {
        const lat = alert.location?.lat;
        const lng = alert.location?.lng;
        const responder = alert.responderId?.name;
        return (
          <li key={alert._id} className="prof-sos-item">
            <span className={`prof-sos-status`} data-status={alert.status}>
              {alert.status}
            </span>
            <div className="prof-sos-main">
              <p className="prof-sos-when">{formatWhen(alert.createdAt)}</p>
              <p className="prof-hero-meta">
                {alert.area || 'No area'}
                {typeof lat === 'number' && typeof lng === 'number'
                  ? ` · ${lat.toFixed(5)}, ${lng.toFixed(5)}`
                  : ''}
              </p>
              {responder ? <p className="prof-hero-meta">Responded by {responder}</p> : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function MyProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [sosAlerts, setSosAlerts] = useState([]);
  const [sosLoading, setSosLoading] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await apiFetch(apiUrl('/profile/me'));
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not load your profile');
      setProfile(data.profile);
      updateSessionUser(data.profile);
      if (data.profile?.status === 'Active') {
        setSosLoading(true);
        try {
          const sosRes = await apiFetch(apiUrl('/sos/mine?limit=20'));
          const sosData = await sosRes.json().catch(() => ({}));
          if (sosRes.ok) setSosAlerts(sosData.alerts || []);
        } finally {
          setSosLoading(false);
        }
      } else {
        setSosAlerts([]);
      }
    } catch (e) {
      setError(e.message || 'Could not load your profile');
    }
  }, []);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const saveDetails = async (values) => {
    try {
      const res = await apiFetch(apiUrl('/profile/me'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { error: data.error || 'Could not save', errors: data.errors };
      setProfile(data.profile);
      updateSessionUser(data.profile);
      setEditing(false);
      setNotice('Your details have been updated.');
      return null;
    } catch {
      return { error: 'Network error. Try again.' };
    }
  };

  const isActive = profile?.status === 'Active';

  return (
    <div className="prof-page">
      <div className="prof-shell">
        <header className="prof-header">
          <div>
            <h1 className="prof-title">My profile</h1>
            <p className="prof-sub">Sangram Sena member account</p>
          </div>
        </header>

        {loading ? <p className="prof-muted">Loading…</p> : null}
        {error ? (
          <p className="prof-error" role="alert">
            {error}
          </p>
        ) : null}

        {profile && !isActive ? (
          <ApplicationStatus profile={profile} onRefresh={refresh} refreshing={refreshing} />
        ) : null}

        {profile && isActive ? (
          <>
            <section className="prof-hero">
              <SelfieImage
                userId={profile.id}
                hasPhoto={Boolean(profile.selfiePath)}
                alt=""
                className="prof-avatar"
                placeholder={
                  <div className="prof-avatar placeholder" aria-hidden>
                    {profile.name?.[0] || '?'}
                  </div>
                }
              />
              <div className="prof-hero-info">
                <span className="prof-badge active">Approved</span>
                <h2 className="prof-name">{profile.name}</h2>
                <p className="prof-hero-meta">{profile.email}</p>
                <p className="prof-hero-meta">
                  <span className="prof-mono">{profile.memberId}</span> · {profile.role}
                  {profile.area ? ` · ${profile.area}` : ''}
                </p>
              </div>
            </section>

            <div className="prof-actions">
              <Link className="prof-btn primary" to="/profile/card">
                Download ID card
              </Link>
              {!editing ? (
                <button
                  type="button"
                  className="prof-btn ghost"
                  onClick={() => {
                    setNotice('');
                    setChangingPassword(false);
                    setEditing(true);
                  }}
                >
                  Edit details
                </button>
              ) : null}
              {!changingPassword ? (
                <button
                  type="button"
                  className="prof-btn ghost"
                  onClick={() => {
                    setNotice('');
                    setEditing(false);
                    setChangingPassword(true);
                  }}
                >
                  Change password
                </button>
              ) : null}
            </div>

            {notice ? <p className="prof-notice">{notice}</p> : null}

            {changingPassword ? (
              <ChangePassword
                onDone={(msg) => {
                  setChangingPassword(false);
                  setNotice(msg);
                }}
              />
            ) : null}

            <ProfileDetails
              profile={profile}
              editing={editing}
              onSave={saveDetails}
              onCancel={() => setEditing(false)}
            />

            <section className="prof-sos-section" aria-labelledby="prof-sos-heading">
              <h3 id="prof-sos-heading" className="prof-panel-title">
                Your SOS history
              </h3>
              <p className="prof-sub">Alerts you sent with the emergency button, including resolved ones.</p>
              <SosHistory alerts={sosAlerts} loading={sosLoading} />
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default MyProfile;
